/** The rules of an activity's visit duration (plan §3 Visit card "duration (h + m range,
 *  min ≤ max)", §10.0 C4): `min_duration` / `max_duration` are stored in HOURS
 *  (`DecimalField(max_digits=5, decimal_places=2)`) and edited as hours + minutes. Pure;
 *  the widget and whatever blocks the save read the same functions. */
import { joinHours, splitHours } from '../../time/hours';

export interface DurationRangeValue {
  /** Hours, as stored; null = not set. */
  min: number | null;
  max: number | null;
}

export type DurationBound = 'min' | 'max';

export const BOUND_LABEL: Readonly<Record<DurationBound, string>> = {
  min: 'Minimum',
  max: 'Maximum',
};

/** The column's ceiling: five digits, two of them decimals. */
export const MAX_DURATION_HOURS = 999.99;

/** What one bound's two inputs hold. */
export interface BoundText {
  h: string;
  m: string;
}

/** A stored value as its inputs show it: both empty when not set; the minutes empty on a
 *  whole hour and the hours empty under an hour (their placeholder then reads 0), so
 *  clearing the one filled input clears the bound. */
export function boundText(hours: number | null): BoundText {
  if (hours === null || !Number.isFinite(hours) || hours < 0) return { h: '', m: '' };
  const { h, m } = splitHours(hours);
  return { h: h === 0 && m > 0 ? '' : String(h), m: m === 0 ? '' : String(m) };
}

/** Which input of the bound a problem is in. */
export type BoundPart = 'h' | 'm' | 'both';

export type BoundParse =
  | { ok: true; hours: number | null }
  | { ok: false; part: BoundPart; message: string };

const HOURS_RE = /^\d+(?:[.,]\d+)?$/;
const MINUTES_RE = /^\d+$/;

/** One bound's inputs → stored hours. Both empty is "not set"; the hours may carry a
 *  fraction (1.5 h reads back as 1 h 30 min), the minutes are whole, 0–59. */
export function parseBound(t: BoundText): BoundParse {
  const h = t.h.trim();
  const m = t.m.trim();
  if (h === '' && m === '') return { ok: true, hours: null };
  if (h !== '' && !HOURS_RE.test(h)) {
    return { ok: false, part: 'h', message: 'hours must be a number' };
  }
  if (m !== '' && (!MINUTES_RE.test(m) || Number(m) > 59)) {
    return { ok: false, part: 'm', message: 'minutes run from 0 to 59' };
  }
  const hours = joinHours(h === '' ? 0 : Number(h.replace(',', '.')), m === '' ? 0 : Number(m));
  if (hours > MAX_DURATION_HOURS)
    return { ok: false, part: 'both', message: 'at most 999 h 59 min' };
  return { ok: true, hours };
}

const asHours = (v: unknown): number | null => {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN;
  return Number.isFinite(n) ? n : null;
};

/** The record editor's `duration` value (`{min, max}` in hours) as the widget reads it:
 *  each bound a number, else not set (a decimal sent as text reads as its number). */
export function asDurationValue(raw: unknown): DurationRangeValue {
  const o = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  return { min: asHours(o.min), max: asHours(o.max) };
}

/** The range rule a save must pass: the minimum is not longer than the maximum. */
export function durationRangeError(v: DurationRangeValue): string | null {
  if (v.min !== null && v.max !== null && v.min > v.max) {
    return 'The minimum is longer than the maximum';
  }
  return null;
}
